/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH
 * under one or more contributor license agreements. Licensed under a proprietary license.
 * See the License.txt file for more information. You may not use this file
 * except in compliance with the proprietary license.
 */

import {ReactNode, createContext, useEffect, useState} from 'react';
import {Loading} from '@carbon/react';

import {get, ErrorResponse} from 'request';
import {showError} from 'notifications';

type WebappEndpoints = {
  [key: string]: {
    endpoint: string;
    engineName: string;
  };
};

interface Onboarding {
  enabled: boolean;
  appCuesScriptUrl: string;
}

export type UiConfig = {
  emailEnabled: boolean;
  sharingEnabled: boolean;
  tenantsAvailable: boolean;
  optimizeVersion: string;
  optimizeDocsVersion: string;
  webappsEndpoints: WebappEndpoints;
  webhooks: string[];
  logoutHidden: boolean;
  exportCsvLimit: number;
  maxNumDataSourcesForReport: number;
  onboarding: Onboarding;
  optimizeDatabase: 'opensearch' | 'elasticsearch';
  userTaskAssigneeAnalyticsEnabled: boolean;
  optimizeProfile: string;
};

let globalConfig: UiConfig;
const awaiting: Array<(config: Record<string, unknown>) => void> = [];

interface ConfigContextProps {
  config: UiConfig;
  loadConfig: () => Promise<void>;
}

export const configContext = createContext<ConfigContextProps | null>(null);
export function ConfigProvider({children}: {children: ReactNode}): JSX.Element {
  const [config, setConfig] = useState<UiConfig>();

  const loadConfig = async () => {
    try {
      const response = await get('api/ui-configuration');
      const config = await response.json();

      setConfig(config);
      globalConfig = config;
      awaiting.forEach((cb) => cb(config));
      awaiting.length = 0;
    } catch (err) {
      showError(err as ErrorResponse);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  if (!config) {
    return <Loading />;
  }

  return <configContext.Provider value={{config, loadConfig}}>{children}</configContext.Provider>;
}

export function createAccessorFunction<T>(property: keyof UiConfig): () => Promise<T> {
  return async function (): Promise<T> {
    if (globalConfig) {
      return globalConfig[property] as T;
    }

    return new Promise((resolve) => {
      awaiting.push((config) => resolve(config[property] as T));
    });
  };
}

export const isEmailEnabled = createAccessorFunction<boolean>('emailEnabled');
export const isSharingEnabled = createAccessorFunction<boolean>('sharingEnabled');
export const areTenantsAvailable = createAccessorFunction<boolean>('tenantsAvailable');
export const getOptimizeVersion = createAccessorFunction<string>('optimizeVersion');
export const getDocsVersion = createAccessorFunction<string>('optimizeDocsVersion');
export const getWebappEndpoints = createAccessorFunction<WebappEndpoints>('webappsEndpoints');
export const getWebhooks = createAccessorFunction<string[]>('webhooks');
export const getOptimizeProfile = async () => 'platform';
export const isLogoutHidden = createAccessorFunction<boolean>('logoutHidden');
export const getExportCsvLimit = createAccessorFunction<number>('exportCsvLimit');
export const getMaxNumDataSourcesForReport = createAccessorFunction<number>(
  'maxNumDataSourcesForReport'
);
export const getOnboardingConfig = createAccessorFunction<Onboarding>('onboarding');
export const getOptimizeDatabase =
  createAccessorFunction<UiConfig['optimizeDatabase']>('optimizeDatabase');
export const getUserTaskAssigneeAnalyticsEnabled = createAccessorFunction<boolean>(
  'userTaskAssigneeAnalyticsEnabled'
);

export {default as newReport} from './newReport.json';
